import { filter, isString, isEmpty, find, toNumber, toInteger, isArray, head, last, isBoolean, map, isNil, isNaN, isUndefined, get } from 'lodash';

// local dependencies
import { DATA_TYPES, BLANK_VALUE_SYMBOL } from 'constants/common';
import { getLabel } from 'utils/getLabel';

/**
 * @desc Resolve dispay value based on data type.
 * import { getDisplayValue } from 'resolvers/formatValue';
 */
export const getDisplayValue = ( { type, value, returnType = null, options = [], config={} } = {} ) => {
    switch( type ) {

        // text -> string
        case DATA_TYPES.TEXT: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            // if an array of string
            if( isArray( value ) ) {
                return map( value, _val => `${ _val }` );
            }

            /*-----*/

            return `${ value }`;
        }

        /*---------------*/

        // phone (string or {type, value})
        case DATA_TYPES.PHONE: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/
            
            if( isString( value ) ) {
                return value;
            }

            /*-----*/

            if( DATA_TYPES.TEXT === returnType ) {
                return filter( [ value.type, value.value ], Boolean ).join( ', ' );
            }

            /*-----*/

            return filter( [ value.type, value.value ], Boolean );
        }

        /*---------------*/

        // email (string)
        case DATA_TYPES.EMAIL: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            return `${ value }`;
        }

        /*---------------*/

        // organization ({name, code})
        case DATA_TYPES.ORG: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            if( DATA_TYPES.TEXT === returnType ) {
                if( isArray( value ) ) {
                    return filter( [ head( value ), last( value ) ], Boolean ).join( ', ' );
                }
                
                return filter( [ value.name, value.code ], Boolean ).join( ', ' );
            } else if( DATA_TYPES.ARRAY === returnType ) {
                if( isArray( value ) ) {
                    return filter( [ head( value ), last( value ) ], Boolean );
                }
                
                return filter( [ value.name, value.code ], Boolean );
            }

            /*-----*/

            return value;
        }

        /*---------------*/

        // address ({line1, line2, city, state, zip})
        case DATA_TYPES.ADDRESS: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            if( DATA_TYPES.TEXT === returnType ) {
                return filter( [ value.line1, value.line2, value.city, value.state, value.zip ], Boolean ).join( ', ' );
            }

            /*-----*/

            return filter( [ value.line1, value.line2, value.city, value.state, value.zip ], Boolean );
        }

        /*---------------*/

        // single select (string)
        case DATA_TYPES.STEP_PROGRESS: {
            
             // if `value` is empty, return `hyphen` character
             if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            if( ! isNil( get( value, 'stepError', null ) ) && '' !== get( value, 'stepError', null ) ) {
                const selectedOption = find( config.options, { value: value.stepError } );
                value.displayValue = selectedOption ? getLabel( selectedOption.title ): BLANK_VALUE_SYMBOL;

            } else {

                const _selectedOption = options[ toInteger( value.currentStep ) - 1 ];
                value.displayValue = _selectedOption ? getLabel( _selectedOption.title ): BLANK_VALUE_SYMBOL;
            }

            return value;

        }

        /*---------------*/

        case DATA_TYPES.SINGLE_SELECT: {

            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) && ! isBoolean( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            const selectedOption = find( options, { value: value } );

            return isUndefined( selectedOption ) ? BLANK_VALUE_SYMBOL: getLabel( selectedOption.title );
        }

        /*---------------*/

        case DATA_TYPES.MULTI_SELECT: {
            
            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) && ! isBoolean( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            /**
             * the mult select value extraction is only done for single values
             * i.e: not array
             * TO-DO: update the check to loop through values if value is an array
             */
            const selectedOption = /*! isArray( value ) ?*/ find( options, { value: value } );

            return isUndefined( selectedOption ) ? BLANK_VALUE_SYMBOL: getLabel( selectedOption.title );
        }

         /*---------------*/

        case DATA_TYPES.DISTANCE: {

            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) || isEmpty( get( value, 'value' ) ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            const distanceValue = get( value, 'value' );
            const distanceUnit = get( value, 'unit' );
            const selectedOption = find( options, { value: distanceUnit } );
            const unitTitle = 1 < toNumber( distanceValue ) ? get( selectedOption, 'altTitle' ) : get( selectedOption, 'title' );

            return `${ distanceValue } ${ getLabel( unitTitle ) }`;
        }
        
        /*---------------*/

        // currency
        case DATA_TYPES.CURRENCY: {

            if ( isNaN( toNumber( value ) ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            return `$${ toNumber( value ).toFixed( 2 ) }`;
        }

        /*---------------*/

        // return value back
        default: {

            // if `value` is empty, return `hyphen` character
            if( isEmpty( value ) ) {
                return BLANK_VALUE_SYMBOL;
            }

            /*-----*/

            return value;
        }
    }
};

/**
 * @desc Get a proper `Number` value.
 * import { castNumber } from 'resolvers/formatValue';
 */
export const castNumber = ( { value, castInteger = false } = {} ) => {
    
    // cast `value` to a number
    let returnValue = toNumber( value );

    // if `returnValue` is `NaN` or `Infinity`, return `0`
    if( ! isFinite( returnValue ) ) {
        returnValue = 0;
    }

    // optimize return value
    if( castInteger ) {
        returnValue = toInteger( returnValue );
    }

    // return value
    return returnValue;
    
};
